import { useState, useCallback, useRef, useEffect } from "react";
import { DEFAULT_STATUS } from "../constants/status.constants";
import { useEmploymentStatuses, useStudentOutcomeCurrent } from "../services/queries";
import { useUpdateStudentOutcome } from "../services/mutations";

export const useStatus = (enabled: boolean = true) => {
	const [selectedStatus, setSelectedStatus] = useState<string>(DEFAULT_STATUS);
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [pendingStatus, setPendingStatus] = useState<string | null>(null);
	const [pendingApiCode, setPendingApiCode] = useState<string | undefined>(undefined);
	const [pendingStatusId, setPendingStatusId] = useState<string | undefined>(undefined);
	const dropdownRef = useRef<HTMLDivElement | null>(null);

	const { employmentStatuses, loading: loadingStatuses } = useEmploymentStatuses();
	const { outcomeCurrent, refetch: refetchOutcomeCurrent } = useStudentOutcomeCurrent(enabled);
	const { mutate: updateStudentOutcome } = useUpdateStudentOutcome();

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
				setIsDropdownOpen(false);
			}
		};

		if (isDropdownOpen) {
			document.addEventListener("mousedown", handleClickOutside);
		}

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [isDropdownOpen]);

	useEffect(() => {
		const backendStatusName = outcomeCurrent?.outcomeStatus?.name;
		if (backendStatusName && backendStatusName.trim() !== "") {
			setSelectedStatus(backendStatusName);
			return;
		}

		if (outcomeCurrent === null) {
			setSelectedStatus(DEFAULT_STATUS);
		}
	}, [outcomeCurrent]);

	const handleStatusSelect = useCallback(
		(status: string, requiresModal: boolean, statusId?: string, apiCode?: string) => {
			if (requiresModal) {
				setPendingStatus(status);
				setPendingApiCode(apiCode);
				setPendingStatusId(statusId);
				setIsModalOpen(true);
				setIsDropdownOpen(false);
				return;
			}

			if (!statusId) {
				setSelectedStatus(status);
				setIsDropdownOpen(false);
				return;
			}

			const currentDate = new Date().toISOString().split("T")[0];

			updateStudentOutcome(
				{
					outcomeStatusId: statusId,
					effectiveFrom: currentDate,
					remarks: "",
				},
				{
					onSuccess: () => {
						setIsDropdownOpen(false);
						refetchOutcomeCurrent();
					},
				},
			);
		},
		[updateStudentOutcome, refetchOutcomeCurrent],
	);

	const handleCloseModal = useCallback(() => {
		setIsModalOpen(false);
		setPendingStatus(null);
		setPendingApiCode(undefined);
		setPendingStatusId(undefined);
	}, []);

	const handleStatusUpdated = useCallback((statusName: string) => {
		setSelectedStatus(statusName);
	}, []);

	const toggleDropdown = useCallback(() => {
		setIsDropdownOpen(prev => !prev);
	}, []);

	return {
		selectedStatus,
		isDropdownOpen,
		isModalOpen,
		pendingStatus,
		pendingApiCode,
		pendingStatusId,
		dropdownRef,
		handleStatusSelect,
		handleCloseModal,
		handleStatusUpdated,
		toggleDropdown,
		employmentStatuses:
			employmentStatuses.length > 0
				? employmentStatuses
				: [{ value: DEFAULT_STATUS, label: DEFAULT_STATUS, requiresModal: false }],
		loadingStatuses,
	};
};
